/**
 * Safely determines the site origin (protocol + host + port) from a given window,
 * falling back to the global `location.origin` if `win` is not provided.
 */
export function getSiteOrigin(win?: Window) {
    try {
        // Prefer the origin from the passed-in window, otherwise fallback to global location
        return win?.location?.origin ?? location.origin;
    } catch {
        // In case `location` access fails (e.g., sandboxed environment), return empty string
        return "";
    }
}

/**
 * Converts a possibly relative URL into an absolute URL using the provided origin.
 */
export function toAbsolute(rawUrl: string, origin: string) {
    return origin && !rawUrl.startsWith("http")
        // If relative, prepend the origin (ensure only one slash between origin and path)
        ? `${origin}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`
        // If already absolute or no origin provided, return as-is
        : rawUrl;
}

/**
 * Normalizes a URL so that it can be consistently compared and deduplicated.
 */
export function normalize(rawUrl: string, origin: string) {
    try {
        //  Ensures the URL is absolute (via `toAbsolute`).
        const u = new URL(toAbsolute(rawUrl, origin)); // Parse into URL object

        // Normalize pathname: default to "/" and strip trailing slash if not root
        let p = u.pathname || "/";
        if (p !== "/" && p.endsWith("/")) p = p.slice(0, -1);

        // Return canonical form: protocol + host + normalized path
        return `${u.protocol}//${u.host}${p}`;
    } catch {
        // Fallback: return trimmed raw string if parsing fails
        return rawUrl.trim();
    }
}
